import {
	Component,
	Input,
	Optional,
	Output,
	OnInit,
	OnDestroy,
	EventEmitter,
	OnChanges,
	SimpleChanges,
	SimpleChange,
	DoCheck,
	KeyValueDiffers,
	KeyValueDiffer,
	forwardRef
} from '@angular/core';
import {
	FormGroup,
	FormControl,
	Validator,
	Validators,
	ControlValueAccessor,
	NG_VALUE_ACCESSOR,
	NG_VALIDATORS
} from '@angular/forms';
import { TranslationService, AutocompleteService, ValueModel } from '../../core';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
	selector: 'sm-select',
	template: require('./sm-select.component.html'),
	providers: [
		{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SmSelectComponent), multi: true },
		{ provide: NG_VALIDATORS, useExisting: forwardRef(() => SmSelectComponent), multi: true }
	]
})
export class SmSelectComponent implements Validator, ControlValueAccessor, OnInit, OnDestroy, OnChanges, DoCheck {
	@Input() type: 'array' | 'number' = 'array';
	@Input() multiple: boolean = false;
	@Input() values: ValueModel[] = [];
	@Input('datasource') dataSource: string = '';
	@Input('datasourceparams') dataSourceParams: { [key: string]: string } = {};
	@Input() cssClass: string = 'form-control';
	@Input() empty: ValueModel = null;
	@Input() addEmpty: boolean = false;
	@Output('change') onChange: EventEmitter<ValueModel[]> = new EventEmitter();
	@Output('loaded') onLoaded: EventEmitter<ValueModel[]> = new EventEmitter();

	control: FormControl;
	loading: boolean = false;
	paramsDiffer: KeyValueDiffer<string, string>;
	private _subs: Subscription[] = [];

	constructor(
		private _autocompleteService: AutocompleteService,
		keyValueDiffers: KeyValueDiffers
	) {
		this.paramsDiffer = keyValueDiffers.find({}).create();
	}

	ngOnChanges(changes: SimpleChanges) {
		let dsChange: SimpleChange = changes['datasource'];
		if (dsChange && !dsChange.isFirstChange()) {
			this._load();
		}
	}

	ngDoCheck() {
		if (this.dataSource && this.dataSourceParams) {
			let changes = this.paramsDiffer.diff(this.dataSourceParams);
			if (changes) {
				this._load();
			}
		}
	}

	ngOnInit() {
		this.control = new FormControl(null);
		this._load();
	}

	ngOnDestroy() {
		this._subs.forEach(s => s.unsubscribe());
	}

	//#region ControlValueAccessor implementation

	writeValue(obj: any): void {
		if (obj) {
			if (this.type == 'number') {
				obj = [obj];
			}

			this.control.setValue(obj, { onlySelf: true, emitEvent: false });
		}
	}

	registerOnChange(fn: any): void {
		this._subs.push(
			this.control.valueChanges.subscribe((changes) => {
				fn(this._getValue());
			}));
	}

	registerOnTouched(fn: any): void {
		this._subs.push(
			this.control.statusChanges.subscribe(s => {
				if (this.control.touched) {
					fn(this._getValue());
				}
			}));
	}

	setDisabledState(isDisabled: boolean): void {
		isDisabled ? this.control.disable() : this.control.enable();
	}

	//#endregion ControlValueAccessor implementation

	//#region Validator

	validate(c: FormGroup) {
		if (this.control.valid) {
			return null;
		}

		return {
			smSelect: this.control.errors || c.errors
		};
	}

	//#endregion Validator

	private _load() {
		if (this.dataSource) {
			this.loading = true;

			this._autocompleteService
				.get(this.dataSource, { cache: false, parameters: this.dataSourceParams })
				.pipe(finalize(() => this.loading = false))
				.subscribe(
					data => {
						this.values = data;

						if (this.addEmpty) {
							this.values.unshift(this.empty || { value: null, name: '' });
						}

						if (this.values.length > 0) {
							const idx = this.values.findIndex(value => value.value == this._getValue());
							if (idx < 0) {
								this._selectValue(this.values[0]);
							}
						}

						this.onLoaded.emit(this.values);
					},
					error => { },
				);
		}
	}

	private _selectValue(value: ValueModel) {
		this.control.setValue([+value.value]);
	}

	private _isNumber() {
		return this.type == 'number';
	}

	private _getValue() {
		if (this._isNumber()) {
			if (this.control!.value != null && this.control.value.length > 0) {
				return this.control.value[0];
			}
			return 0;
		}
		else {
			return this.control.value;
		}
	}
}


// WEBPACK FOOTER //
// ./node_modules/angular-router-loader/src!./node_modules/angular2-template-loader!./app/shared/components/sm-select.component.ts